import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronLeft, Trash2, Check, AlertTriangle, ExternalLink, CheckCircle2, Send, Image as ImageIcon, Copy, Rows3, Share2 } from 'lucide-react';
import { Card, Btn, Badge, Modal, Field, Input, Select, EmptyState } from '../../components/UI';
import { sendPush } from '../../utils/push';
import { removeByPrefix, CONTENT_BUCKET } from '../../utils/media';
import { taskSetStatus, setCalendarPostFields, markLocalWrite } from '../../utils/db';
import {
  OBJECTIVES, POST_TYPES, uid, nowIso, today, thisMonth, nextMonth, monthLabel, fmtDate,
  isCreator, isReviewer, isDesigner, designerEmployees, reviewerRecipients, empName, clientOptions, calendarPosts, makeNotif,
} from './contentShared';
import { usePresence, PresenceBar } from '../../components/Presence';
import { celebrate } from '../../utils/celebrate';

const inp = { width: '100%', padding: '8px 10px', fontSize: 13, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'var(--font)' };
const ta = { ...inp, resize: 'vertical', lineHeight: 1.5, minHeight: 60 };
const lbl = { fontSize: 10, fontWeight: 400, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 4 };

const DESIGN_BADGE = {
  todo:      { label: 'Not started', color: 'gray' },
  submitted: { label: 'Submitted',   color: 'purple' },
  changes:   { label: 'Changes needed', color: 'orange' },
  approved:  { label: 'Approved',    color: 'green' },
};
const CAL_BADGE = {
  draft:     { label: 'Draft',      color: 'gray' },
  assigned:  { label: 'Assigned',   color: 'blue' },
  in_review: { label: 'In review',  color: 'purple' },
  approved:  { label: 'Approved',   color: 'green' },
};
const ARCHIVED = s => s === 'Reviewed' || s === 'Completed';

function emptyPost() {
  return {
    id: uid(), gridNumber: '', date: '', objective: 'Product Promotion', type: 'Reel',
    postContent: '', inspiration: '', contentLinks: '', reelCoverNote: '', notes: '', caption: '',
    fileLink: '', designedBy: [], designStatus: 'todo', reviewNote: '', reviewBy: '',
  };
}
function emptyGrid() { return { id: uid(), link: '', posts: [emptyPost()] }; }

// Return the calendar's grid-rows, migrating a legacy flat `rows` list into rows of 3.
function getGrids(cal) {
  if (Array.isArray(cal.data.grids)) return cal.data.grids;
  const posts = cal.data.rows || [];
  const grids = [];
  for (let i = 0; i < posts.length; i += 3) {
    grids.push({ id: `${cal.id}-g${grids.length}`, link: i === 0 ? (cal.data.gridFinalLink || '') : '', posts: posts.slice(i, i + 3) });
  }
  return grids.length ? grids : [{ id: `${cal.id}-g0`, link: cal.data.gridFinalLink || '', posts: [emptyPost()] }];
}

export default function CalendarBuilder({ store, persist, showToast, session, onBuildMockup, openCalendarId, onOpenConsumed }) {
  const employees = store.employees || [];
  const clients = clientOptions(store.clients);
  const cals = store.contentCalendars || [];
  const canCreate = isCreator(session, employees);
  const reviewer = isReviewer(session, employees);
  const designer = isDesigner(session, employees);
  const myId = session?.role === 'admin' ? 'admin' : session?.empId;

  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ clientId: '', monthYear: thisMonth(), title: '' });
  const [assign, setAssign] = useState(null);      // {assignedToIds, dueDate, notes}
  const [reject, setReject] = useState(null);      // {calId, postId, reason}
  const [filterClient, setFilterClient] = useState('all');
  const [reviewMode, setReviewMode] = useState(false);
  const [copied, setCopied] = useState(false);

  const selected = cals.find(c => c.id === selectedId) || null;

  // Live presence — managers/designers on the same calendar see each other
  // (shares the '/c/' share-link channel, so both sides are visible).
  const presenceMe = { id: myId, name: empName(employees, myId) || 'Admin' };
  const { peers, setEditing } = usePresence(selectedId ? 'cal:' + selectedId : null, presenceMe);
  useEffect(() => { setEditing(!!selectedId); }, [selectedId, setEditing]);

  // Flush any pending atomic post-content write when leaving the calendar, hiding the
  // tab, or unloading — so the last edit in the debounce window is never lost.
  useEffect(() => {
    const onHide = () => { if (document.hidden) flushAllPosts(); };
    window.addEventListener('beforeunload', flushAllPosts);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('beforeunload', flushAllPosts); document.removeEventListener('visibilitychange', onHide); flushAllPosts(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Deep-link: a notification / push tap asked to open a specific calendar.
  // Wait until it's loaded, then jump straight into its editor (no hunting the list).
  useEffect(() => {
    if (!openCalendarId) return;
    if (!cals.some(c => c.id === openCalendarId)) return;
    setReviewMode(false);
    setSelectedId(openCalendarId);
    onOpenConsumed && onOpenConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCalendarId, cals]);

  const mine = cals.filter(c => {
    if (canCreate) return true;
    const a = c.data?.assignment?.assignedToIds || [];
    if (a.includes(myId)) return true;
    return calendarPosts(c).some(p => (p.designedBy || []).includes(myId));
  });
  const clientsInList = Array.from(new Map(mine.map(c => [c.clientId, c.clientName])).entries());
  const visible = mine
    .filter(c => filterClient === 'all' || c.clientId === filterClient)
    .slice().sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));

  // ── persistence helpers ─────────────────────────────────────────
  function saveCal(updated, extra = {}, opts) {
    const u = { ...updated, updatedAt: nowIso() };
    persist({ ...store, contentCalendars: cals.map(c => c.id === u.id ? u : c), ...extra }, opts);
  }
  function saveGrids(cal, grids, extra, opts) { saveCal({ ...cal, data: { ...cal.data, grids } }, extra, opts); }
  // Content fields are written ATOMICALLY per-post via set_calendar_post_fields — the
  // whole-store merge no longer touches them for existing posts, so a stale save can
  // never revert a caption/content. Debounce (~500ms, the old cadence) so typing
  // coalesces into one write; flush on leaving the calendar / tab hide / unload.
  const CAL_CONTENT_FIELDS = ['caption', 'postContent', 'inspiration', 'contentLinks', 'reelCoverNote', 'notes', 'objective', 'date', 'type', 'awaitingClient', 'awaitingNote'];
  // Same protection as the Mockup Studio captions (see MockupStudio.js): writes for a
  // post go one at a time so an older one can never land after a newer one, and what
  // was typed (`overlay`) is shown in preference to a live reload's older copy until
  // the database has caught up — so deleted text can't come back.
  const postWrite = useRef({ pending: {}, timers: {}, inflight: {} });
  const [overlay, setOverlay] = useState({});        // 'calId|postId' → { fields, acked }
  function sendPost(calId, postId) {
    const key = calId + '|' + postId;
    const st = postWrite.current;
    if (st.inflight[key]) return;
    const fields = st.pending[key];
    if (!fields || !Object.keys(fields).length) return;
    delete st.pending[key];
    st.inflight[key] = true;
    markLocalWrite();
    setCalendarPostFields(calId, postId, fields)
      .catch(() => {
        st.pending[key] = { ...fields, ...(st.pending[key] || {}) };
        showToast('That change didn’t save yet — retrying…', 'error');
        if (!st.timers[key]) st.timers[key] = setTimeout(() => { delete st.timers[key]; sendPost(calId, postId); }, 3000);
      })
      .finally(() => {
        st.inflight[key] = false;
        markLocalWrite();
        if (st.pending[key] && !st.timers[key]) sendPost(calId, postId);
        else if (!st.pending[key]) setOverlay(o => (o[key] ? { ...o, [key]: { ...o[key], acked: Date.now() } } : o));
      });
  }
  function flushPost(calId, postId) {
    const key = calId + '|' + postId;
    if (postWrite.current.timers[key]) { clearTimeout(postWrite.current.timers[key]); delete postWrite.current.timers[key]; }
    sendPost(calId, postId);
  }
  function flushAllPosts() { Object.keys(postWrite.current.pending).forEach(key => { const i = key.indexOf('|'); flushPost(key.slice(0, i), key.slice(i + 1)); }); }
  function updatePost(cal, postId, patch, extra) {
    const grids = getGrids(cal).map(g => ({ ...g, posts: g.posts.map(p => p.id === postId ? { ...p, ...patch } : p) }));
    saveGrids(cal, grids, extra);
    const cp = {};
    for (const k of CAL_CONTENT_FIELDS) if (k in patch) cp[k] = patch[k];
    if (Object.keys(cp).length) {
      const key = cal.id + '|' + postId;
      setOverlay(o => ({ ...o, [key]: { fields: { ...((o[key] && o[key].fields) || {}), ...cp }, acked: 0 } }));
      postWrite.current.pending[key] = { ...(postWrite.current.pending[key] || {}), ...cp };
      if (postWrite.current.timers[key]) clearTimeout(postWrite.current.timers[key]);
      postWrite.current.timers[key] = setTimeout(() => { delete postWrite.current.timers[key]; sendPost(cal.id, postId); }, 500);
    }
  }
  useEffect(() => {
    setOverlay(o => {
      const keys = Object.keys(o);
      if (!keys.length) return o;
      const st = postWrite.current; const next = {}; let changed = false;
      for (const key of keys) {
        const i = key.indexOf('|'); const cid = key.slice(0, i), pid = key.slice(i + 1);
        const busy = st.inflight[key] || st.pending[key] || st.timers[key];
        const c = cals.find(x => x.id === cid);
        const post = c ? getGrids(c).flatMap(g => g.posts || []).find(p => p.id === pid) : null;
        const caughtUp = post && Object.keys(o[key].fields).every(f => JSON.stringify(post[f] ?? '') === JSON.stringify(o[key].fields[f] ?? ''));
        if (!busy && (caughtUp || (o[key].acked && Date.now() - o[key].acked > 8000))) { changed = true; continue; }
        next[key] = o[key];
      }
      return changed ? next : o;
    });
  }, [store.contentCalendars]); // eslint-disable-line react-hooks/exhaustive-deps
  function setGridLink(cal, gridId, link) {
    saveGrids(cal, getGrids(cal).map(g => g.id === gridId ? { ...g, link } : g));
  }
  function addPost(cal) {
    const grids = getGrids(cal).map(g => ({ ...g, posts: [...g.posts] }));
    const last = grids[grids.length - 1];
    if (last && last.posts.length < 3) last.posts.push(emptyPost());
    else grids.push({ id: uid(), link: '', posts: [emptyPost()] });   // 4th post → new row
    saveGrids(cal, grids);
  }
  function addGrid(cal) { saveGrids(cal, [...getGrids(cal), emptyGrid()]); }
  function deletePost(cal, gridId, postId) {
    const before = getGrids(cal);
    let grids = before.map(g => g.id === gridId ? { ...g, posts: g.posts.filter(p => p.id !== postId) } : g);
    grids = grids.filter(g => g.posts.length > 0);
    if (!grids.length) grids = [emptyGrid()];
    // Name exactly what the user removed. Removing the last post in a row also removes the
    // row, so that row id goes on the list too — otherwise the save would infer it.
    const kept = new Set(grids.map(g => g.id));
    const goneGrids = before.filter(g => !kept.has(g.id)).map(g => g.id);
    saveGrids(cal, grids, undefined, { deletedIds: {
      calendarPosts: { [cal.id]: [postId] },
      ...(goneGrids.length ? { calendarGrids: { [cal.id]: goneGrids } } : {}),
    } });
  }
  function deleteGrid(cal, gridId) {
    const before = getGrids(cal);
    let grids = before.filter(g => g.id !== gridId);
    if (!grids.length) grids = [emptyGrid()];
    const gone = before.find(g => g.id === gridId);
    // The row and every post in it are named explicitly, so a filled row deletes cleanly.
    saveGrids(cal, grids, undefined, { deletedIds: {
      calendarGrids: { [cal.id]: [gridId] },
      ...(gone && gone.posts && gone.posts.length ? { calendarPosts: { [cal.id]: gone.posts.map(p => p.id) } } : {}),
    } });
  }

  function createCalendar() {
    if (!newForm.clientId) return showToast('Pick a client.', 'error');
    const client = clients.find(c => c.id === newForm.clientId);
    const cal = {
      id: uid(), clientId: newForm.clientId, clientName: client?.name || '',
      monthYear: newForm.monthYear, status: 'draft', createdAt: nowIso(), updatedAt: nowIso(),
      createdBy: myId,
      data: {
        title: newForm.title || `${monthLabel(newForm.monthYear)} Calendar`,
        taskId: '', assignment: { assignedToIds: [], dueDate: '', notes: '' },
        grids: [emptyGrid()],
      },
    };
    persist({ ...store, contentCalendars: [...cals, cal] });
    setShowNew(false); setNewForm({ clientId: '', monthYear: thisMonth(), title: '' });
    setSelectedId(cal.id);
  }

  async function deleteCalendar(cal) {
    if (!window.confirm('Delete this calendar? Any uploaded images will be removed too.')) return;
    try { await removeByPrefix(CONTENT_BUCKET, `${cal.clientId}/${cal.id}`); } catch (e) { /* non-fatal */ }
    persist({ ...store, contentCalendars: cals.filter(c => c.id !== cal.id) }, { deletedIds: { contentCalendars: [cal.id] } });
    setSelectedId(null);
    showToast('Calendar deleted.');
  }

  // The designers' live working link (served at /c/<id>). No publish step —
  // it reads the calendar live, so the link always works.
  const workLink = (c) => `${window.location.origin}/c/${c.id}`;
  function copyWorkLink(cal) {
    navigator.clipboard.writeText(workLink(cal));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    showToast('Designer link copied.');
  }

  // ── mark-done → assign → auto-create task ───────────────────────
  function openAssign() {
    setAssign({ assignedToIds: selected.data.assignment?.assignedToIds || [], dueDate: selected.data.assignment?.dueDate || today(), notes: selected.data.assignment?.notes || '' });
  }
  function confirmAssign() {
    if (!assign.assignedToIds.length) return showToast('Assign at least one designer.', 'error');
    if (!assign.dueDate) return showToast('Pick a due date.', 'error');
    const cal = selected;
    const assignerName = empName(employees, myId);
    const task = {
      id: uid(), clientName: cal.clientName,
      taskName: `Content Calendar — ${monthLabel(cal.monthYear)}`,
      category: 'Calendar',
      assigneeId: assign.assignedToIds[0], assigneeName: empName(employees, assign.assignedToIds[0]),
      assignedToIds: assign.assignedToIds, dueDate: assign.dueDate, status: 'Not Started',
      notes: `Open your calendar to work on it:\n${workLink(cal)}\n\nClaim each post you take, paste your asset link, and mark it done — it updates live for the managers to review.${assign.notes ? `\n\n${assign.notes}` : ''}`,
      hasSubTasks: false, subTasks: [], workingOn: [], completedBy: [], assignMode: 'all',
      createdAt: nowIso(), createdBy: myId,
    };
    const notifs = assign.assignedToIds.filter(id => id !== myId)
      .map(id => makeNotif(id, `${assignerName} assigned you the ${monthLabel(cal.monthYear)} content calendar for ${cal.clientName} · due ${fmtDate(assign.dueDate)}`, { id: cal.id, name: task.taskName, client: cal.clientName }));
    const updatedCal = { ...cal, status: 'assigned', updatedAt: nowIso(), data: { ...cal.data, taskId: task.id, assignment: assign } };
    persist({
      ...store,
      tasks: [...(store.tasks || []), task],
      contentCalendars: cals.map(c => c.id === cal.id ? updatedCal : c),
      notifications: [...(store.notifications || []), ...notifs],
    });
    assign.assignedToIds.filter(id => id !== myId).forEach(id => sendPush(id, 'New content calendar', `${assignerName} assigned you ${monthLabel(cal.monthYear)} for ${cal.clientName} — tap to open and start`, `/c/${cal.id}`));
    setAssign(null);
    showToast('Calendar assigned + task created.');
  }

  function duplicateCalendar(cal) {
    const my = nextMonth(cal.monthYear);
    const grids = getGrids(cal).map(g => ({
      id: uid(), link: '',
      posts: g.posts.map(p => ({ ...p, id: uid(), fileLink: '', designedBy: [], designStatus: 'todo', reviewNote: '', reviewBy: '' })),
    }));
    const copy = {
      id: uid(), clientId: cal.clientId, clientName: cal.clientName, monthYear: my,
      status: 'draft', createdAt: nowIso(), updatedAt: nowIso(), createdBy: myId,
      data: { title: `${monthLabel(my)} Calendar`, taskId: '', assignment: { assignedToIds: [], dueDate: '', notes: '' }, grids },
    };
    persist({ ...store, contentCalendars: [...cals, copy] });
    setSelectedId(copy.id);
    showToast(`Duplicated to ${monthLabel(my)}.`);
  }

  // ── designer actions ────────────────────────────────────────────
  function toggleWorked(cal, post) {
    const has = (post.designedBy || []).includes(myId);
    updatePost(cal, post.id, { designedBy: has ? post.designedBy.filter(id => id !== myId) : [...(post.designedBy || []), myId] });
  }
  function submitPost(cal, grid, post) {
    celebrate('submit');
    if (!(post.designedBy || []).includes(myId)) return showToast('Tick "I worked on this" first.', 'error');
    if (!post.fileLink && !grid.link) return showToast("Add this post's file link (or the row's grid link) first.", 'error');
    const grids = getGrids(cal).map(g => ({ ...g, posts: g.posts.map(p => p.id === post.id ? { ...p, designStatus: 'submitted', reviewNote: '' } : p) }));
    const status = cal.status === 'assigned' ? 'in_review' : cal.status;
    const submitterName = empName(employees, myId);
    const targets = reviewerRecipients(employees).filter(id => id !== myId);
    const notifs = targets.map(id => makeNotif(id, `${submitterName} submitted a post for review on the ${monthLabel(cal.monthYear)} calendar for ${cal.clientName}`, { id: cal.id, name: cal.data.title, client: cal.clientName }));
    saveGrids({ ...cal, status }, grids, notifs.length ? { notifications: [...(store.notifications || []), ...notifs] } : {});
    targets.forEach(id => sendPush(id, 'Post submitted for review', `${submitterName} · ${cal.clientName} (${monthLabel(cal.monthYear)})`, `/?calendar=${cal.id}`));
    showToast('Submitted for review.');
  }

  // ── reviewer actions ────────────────────────────────────────────
  function approvePost(cal, post) {
    celebrate('approve');
    const grids = getGrids(cal).map(g => ({ ...g, posts: g.posts.map(p => p.id === post.id ? { ...p, designStatus: 'approved', reviewNote: '', reviewBy: myId } : p) }));
    const posts = grids.flatMap(g => g.posts);
    const allApproved = posts.length > 0 && posts.every(p => p.designStatus === 'approved');
    const status = allApproved ? 'approved' : (cal.status === 'assigned' ? 'in_review' : cal.status);
    const extra = {};
    // Tick off this post's item on the calendar's Changes task (if any).
    if (cal.data.changesTaskId) {
      const ct = (store.tasks || []).find(t => t.id === cal.data.changesTaskId);
      if (ct) {
        const subTasks = (ct.subTasks || []).map(s => s.postId === post.id ? { ...s, done: true } : s);
        const allDone = subTasks.length > 0 && subTasks.every(s => s.done);
        extra.tasks = (store.tasks || []).map(t => t.id === ct.id ? { ...t, subTasks, status: allDone ? 'Reviewed' : t.status } : t);
        // status is written atomically (task_merge no longer persists it); subTasks still merge via the whole-store save
        if (allDone && ct.status !== 'Reviewed') taskSetStatus(ct.id, 'Reviewed').catch(() => {});
      }
    }
    const calData = cal.data;
    if (allApproved) {
      // NOTE: the mockup is NOT auto-created on full approval (removed by request).
      // The creator builds it manually with the "Build mockup" button, which seeds
      // from this calendar on demand. (Auto-spawn may be revisited later.)
      const targets = new Set([...(cal.data.assignment?.assignedToIds || []), cal.createdBy].filter(id => id && id !== myId));
      extra.notifications = [...(store.notifications || []), ...[...targets].map(id => makeNotif(id, `All posts approved on the ${monthLabel(cal.monthYear)} calendar for ${cal.clientName} 🎉`, { id: cal.id, name: cal.data.title, client: cal.clientName }))];
    }
    saveGrids({ ...cal, status, data: calData }, grids, extra);
    if (allApproved) [...new Set([...(cal.data.assignment?.assignedToIds || []), cal.createdBy])].filter(id => id && id !== myId).forEach(id => sendPush(id, 'Calendar approved', `${cal.clientName} — ${monthLabel(cal.monthYear)} is fully approved.`, `/?calendar=${cal.id}`));
    showToast(allApproved ? 'All posts approved.' : 'Post approved.');
  }

  // Request changes → flip the post, AND create/append the calendar's "Changes needed" task.
  function confirmReject() {
    if (!reject.reason.trim()) return showToast('Add a reason for the change.', 'error');
    const cal = cals.find(c => c.id === reject.calId) || selected;
    if (!cal) { setReject(null); return; }
    const grids = getGrids(cal);
    let gi = -1, pi = -1, post = null;
    grids.forEach((g, gIdx) => g.posts.forEach((p, pIdx) => { if (p.id === reject.postId) { gi = gIdx; pi = pIdx; post = p; } }));
    if (!post) { setReject(null); return showToast('That post no longer exists.', 'error'); }
    const reason = reject.reason.trim();
    const newGrids = grids.map(g => ({ ...g, posts: g.posts.map(p => p.id === post.id ? { ...p, designStatus: 'changes', reviewNote: reason, reviewBy: myId } : p) }));
    const reviewerName = empName(employees, myId);
    const designerIds = (post.designedBy || []).filter(Boolean);
    const subTask = { id: uid(), title: `Grid ${gi + 1}, Post ${pi + 1}${post.type ? ` · ${post.type}` : ''} — ${reason}`, done: false, postId: post.id };

    let tasks = store.tasks || [];
    const existing = cal.data.changesTaskId ? tasks.find(t => t.id === cal.data.changesTaskId && !ARCHIVED(t.status)) : null;
    let changesTaskId = existing ? existing.id : uid();
    if (existing) {
      const assignedToIds = Array.from(new Set([...(existing.assignedToIds || []), ...designerIds]));
      tasks = tasks.map(t => t.id === existing.id ? { ...t, assignedToIds, assigneeId: existing.assigneeId || assignedToIds[0] || '', subTasks: [...(existing.subTasks || []), subTask], hasSubTasks: true } : t);
    } else {
      tasks = [...tasks, {
        id: changesTaskId, clientName: cal.clientName,
        taskName: `Changes needed — ${monthLabel(cal.monthYear)}`, category: 'Post',
        assigneeId: designerIds[0] || '', assigneeName: empName(employees, designerIds[0]),
        assignedToIds: designerIds, dueDate: today(), status: 'Not Started',
        notes: `Changes requested on the ${monthLabel(cal.monthYear)} calendar for ${cal.clientName}. Each item below is a grid + post number with what to fix.`,
        hasSubTasks: true, subTasks: [subTask], workingOn: [], completedBy: [],
        createdAt: nowIso(), createdBy: myId,
      }];
    }
    const targets = new Set([...designerIds, cal.createdBy].filter(id => id && id !== myId));
    const msg = `Changes requested: ${cal.clientName} · Grid ${gi + 1}, Post ${pi + 1} — ${reason}`;
    const notifs = [...targets].map(id => makeNotif(id, msg, { id: cal.id, name: cal.data.title, client: cal.clientName }));
    saveGrids({ ...cal, status: 'in_review', data: { ...cal.data, changesTaskId } }, newGrids, { tasks, notifications: [...(store.notifications || []), ...notifs] });
    [...targets].forEach(id => sendPush(id, 'Changes requested', `${reviewerName}: ${reason}`, `/?calendar=${cal.id}`));
    setReject(null);
    showToast(existing ? "Change added to the designer's task." : 'Change request sent + task created.');
  }

  // ═══════════════ LIST VIEW ═══════════════
  if (!selected) {
    // Reviewer queue: every submitted post across the visible calendars, ready to action inline.
    const queueItems = [];
    visible.forEach(c => getGrids(c).forEach((g, gi) => g.posts.forEach((p, pi) => { if (p.designStatus === 'submitted') queueItems.push({ cal: c, gi, pi, post: p, link: g.link }); })));

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 30, fontFamily: 'var(--serif)', fontWeight: 400, letterSpacing: '-0.01em', color: 'var(--text)' }}>Calendars</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>{canCreate ? 'Plan the month by rows, assign designers, review posts.' : 'Calendars assigned to you — add your links and mark what you worked on.'}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {reviewer && (
              <div style={{ display: 'inline-flex', gap: 3, padding: 3, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                {[['calendars', 'Calendars'], ['review', `To review${queueItems.length ? ` (${queueItems.length})` : ''}`]].map(([id, label]) => (
                  <button key={id} onClick={() => setReviewMode(id === 'review')} style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontFamily: 'var(--font)', background: (reviewMode ? 'review' : 'calendars') === id ? 'var(--bg2)' : 'transparent', color: (reviewMode ? 'review' : 'calendars') === id ? 'var(--text)' : 'var(--text3)', boxShadow: (reviewMode ? 'review' : 'calendars') === id ? 'var(--shadow-soft)' : 'none' }}>{label}</button>
                ))}
              </div>
            )}
            {clientsInList.length > 1 && !reviewMode && (
              <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 170 }}>
                <option value="all">All clients</option>
                {clientsInList.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            )}
            {canCreate && !reviewMode && <Btn variant="primary" onClick={() => setShowNew(true)}><Plus size={15} />New calendar</Btn>}
          </div>
        </div>

        {reviewMode && reviewer ? (
          queueItems.length === 0
            ? <Card><EmptyState message="Nothing waiting for review right now 🎉" /></Card>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {queueItems.map(({ cal: c, gi, pi, post, link }) => (
                <Card key={c.id + post.id} style={{ padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', borderLeft: '3px solid var(--accent)' }}>
                  <div style={{ flex: '1 1 280px', minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{c.clientName} · {monthLabel(c.monthYear)} <span style={{ color: 'var(--text3)', fontWeight: 500 }}>· Grid {gi + 1}, Post {pi + 1}</span></div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{post.type}{(post.designedBy || []).length ? ` · by ${post.designedBy.map(id => empName(employees, id)).join(', ')}` : ''}</div>
                    {post.caption && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, maxWidth: 560, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.caption}</div>}
                    {(post.fileLink || link) && <a href={post.fileLink || link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent2)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 4 }}><ExternalLink size={13} />Open file</a>}
                  </div>
                  <Btn size="sm" variant="ghost" onClick={() => setSelectedId(c.id)}>Open calendar</Btn>
                  <Btn size="sm" variant="success" onClick={() => approvePost(c, post)}><Check size={13} />Approve</Btn>
                  <Btn size="sm" variant="danger" onClick={() => setReject({ calId: c.id, postId: post.id, reason: '' })}><AlertTriangle size={13} />Changes</Btn>
                </Card>
              ))}
            </div>
        ) : visible.length === 0
          ? <Card><EmptyState message={canCreate ? 'No calendars yet. Create one to get started.' : 'Nothing assigned to you yet.'} /></Card>
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
            {visible.map(c => {
              const posts = calendarPosts(c);
              const approved = posts.filter(p => p.designStatus === 'approved').length;
              const cb = CAL_BADGE[c.status] || CAL_BADGE.draft;
              return (
                <Card key={c.id} hover style={{ padding: 18, cursor: 'pointer' }}>
                  <div onClick={() => setSelectedId(c.id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
                      <div style={{ fontWeight: 400, fontSize: 15, fontFamily: 'var(--serif)', color: 'var(--text)', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.25 }}>{c.clientName}</div>
                      <span style={{ flexShrink: 0 }}><Badge label={cb.label} color={cb.color} /></span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{monthLabel(c.monthYear)}</div>
                    <div style={{ fontSize: 12, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: 'var(--text3)', marginTop: 10 }}>{posts.length} posts · {approved} approved</div>
                    <div style={{ height: 5, borderRadius: 5, background: 'var(--bg4)', marginTop: 8, overflow: 'hidden' }}>
                      <div className="bar-grow" style={{ height: '100%', width: `${posts.length ? (approved / posts.length) * 100 : 0}%`, background: 'var(--accent)' }} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>}

        <Modal open={showNew} onClose={() => setShowNew(false)} title="New content calendar">
          <Field label="Client">
            <Select value={newForm.clientId} onChange={e => setNewForm(f => ({ ...f, clientId: e.target.value }))}>
              <option value="">Select a client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Month"><Input type="month" value={newForm.monthYear} onChange={e => setNewForm(f => ({ ...f, monthYear: e.target.value }))} /></Field>
          <Field label="Title (optional)"><Input value={newForm.title} onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. July Calendar" /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Btn onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={createCalendar}>Create</Btn>
          </div>
        </Modal>

        {/* Request-changes modal (used by the review queue) */}
        <Modal open={!!reject} onClose={() => setReject(null)} title="Request changes" width={440}>
          {reject && <>
            <Field label="What needs to change?"><textarea style={ta} autoFocus value={reject.reason} onChange={e => setReject(r => ({ ...r, reason: e.target.value }))} placeholder="Be specific — this becomes a subtask on the designer's task." /></Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn onClick={() => setReject(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={confirmReject}>Send change request</Btn>
            </div>
          </>}
        </Modal>
      </div>
    );
  }

  // ═══════════════ EDITOR VIEW ═══════════════
  const cal = selected;
  // What was typed wins until the database has it (see `overlay` above).
  const grids = getGrids(cal).map(g => ({ ...g, posts: (g.posts || []).map(p => { const o = overlay[cal.id + '|' + p.id]; return o ? { ...p, ...o.fields } : p; }) }));
  const editable = canCreate && (cal.status === 'draft' || cal.status === 'assigned' || cal.status === 'in_review');
  const cb = CAL_BADGE[cal.status] || CAL_BADGE.draft;
  let postNo = 0;   // running post counter across rows

  return (
    <div>
      <button onClick={() => setSelectedId(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
        <ChevronLeft size={16} />All calendars
      </button>
      {peers.length > 1 && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}><PresenceBar users={peers} meId={myId} /></div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 26, fontFamily: 'var(--serif)', fontWeight: 400, letterSpacing: '-0.01em', color: 'var(--text)', minWidth: 0, overflowWrap: 'anywhere' }}>{cal.clientName} · {monthLabel(cal.monthYear)}</h2>
          <span style={{ flexShrink: 0 }}><Badge label={cb.label} color={cb.color} /></span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canCreate && cal.status === 'draft' && <Btn variant="success" onClick={openAssign}><Send size={14} />Mark done & assign</Btn>}
          {canCreate && cal.status !== 'draft' && cal.status !== 'approved' && <Btn variant="ghost" onClick={openAssign}>Reassign</Btn>}
          {canCreate && cal.status === 'approved' && <Btn variant="primary" onClick={() => onBuildMockup && onBuildMockup(cal)}><ImageIcon size={14} />Build mockup</Btn>}
          {canCreate && <Btn variant="secondary" onClick={() => copyWorkLink(cal)} title="Copy the designers' live working link"><Share2 size={14} />{copied ? 'Copied ✓' : 'Copy designer link'}</Btn>}
          {canCreate && <Btn variant="ghost" onClick={() => duplicateCalendar(cal)} title="Duplicate to next month"><Copy size={14} />Duplicate</Btn>}
          {canCreate && <Btn variant="danger" onClick={() => deleteCalendar(cal)}><Trash2 size={14} /></Btn>}
        </div>
      </div>

      {canCreate && (cal.status === 'assigned' || cal.status === 'in_review') && (
        <Card style={{ padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Share2 size={14} color="var(--accent2)" />
          <span style={{ fontSize: 12.5, color: 'var(--text3)' }}>Designers' live link:</span>
          <a href={workLink(cal)} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--accent2)', fontWeight: 600, wordBreak: 'break-all' }}>{workLink(cal)}</a>
          <Btn size="sm" onClick={() => copyWorkLink(cal)}>{copied ? <Check size={12} /> : <Copy size={12} />}{copied ? 'Copied' : 'Copy'}</Btn>
          <Btn size="sm" variant="ghost" onClick={() => window.open(workLink(cal), '_blank')}><ExternalLink size={12} />Open</Btn>
        </Card>
      )}

      {/* Grid-rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {grids.map((grid, gi) => (
          <Card key={grid.id} style={{ padding: 0, overflow: 'hidden' }}>
            {/* Row header with its own grid link */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--text)' }}><Rows3 size={15} color="var(--accent2)" />Row {gi + 1}</span>
              <div style={{ flex: '1 1 240px', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={inp} value={grid.link || ''} placeholder="Row grid link (Drive / Canva)…" onChange={e => setGridLink(cal, grid.id, e.target.value)} disabled={!designer && !canCreate} />
                {grid.link && <a href={grid.link} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', display: 'flex' }}><ExternalLink size={16} /></a>}
              </div>
              {editable && <button title="Delete row" onClick={() => deleteGrid(cal, grid.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', display: 'flex' }}><Trash2 size={16} /></button>}
            </div>

            {/* Posts in this row */}
            <div style={{ padding: 12, display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, grid.posts.length))},1fr)`, gap: 12 }} className="cal-row-posts">
              {grid.posts.map(post => {
                postNo += 1;
                const db = DESIGN_BADGE[post.designStatus] || DESIGN_BADGE.todo;
                const iWorked = (post.designedBy || []).includes(myId);
                return (
                  <div key={post.id} style={{ border: '1px solid var(--border)', borderLeft: post.designStatus === 'changes' ? '3px solid #C08079' : post.designStatus === 'approved' ? '3px solid var(--accent)' : '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 12, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: 'var(--text)' }}>#{postNo}</strong>
                      <Badge label={post.type} color="teal" />
                      <Badge label={db.label} color={db.color} />
                      {post.awaitingClient && <Badge label="Awaiting client" color="orange" />}
                      <div style={{ flex: 1 }} />
                      {editable && <button title="Delete post" onClick={() => deletePost(cal, grid.id, post.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', display: 'flex' }}><Trash2 size={14} /></button>}
                    </div>
                    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div style={{ flex: 1 }}><div style={lbl}>Date</div><input style={inp} value={post.date} placeholder="Jul 27" disabled={!editable} onChange={e => updatePost(cal, post.id, { date: e.target.value })} /></div>
                        <div style={{ flex: 1 }}><div style={lbl}>Type</div>
                          <select style={inp} value={post.type} disabled={!editable} onChange={e => updatePost(cal, post.id, { type: e.target.value })}>{POST_TYPES.map(o => <option key={o}>{o}</option>)}</select></div>
                      </div>
                      <div><div style={lbl}>Objective</div>
                        <select style={inp} value={post.objective} disabled={!editable} onChange={e => updatePost(cal, post.id, { objective: e.target.value })}>{OBJECTIVES.map(o => <option key={o}>{o}</option>)}</select></div>
                      <div><div style={lbl}>Post content</div><textarea style={ta} value={post.postContent} disabled={!editable} onChange={e => updatePost(cal, post.id, { postContent: e.target.value })} /></div>
                      <div><div style={lbl}>Inspiration</div><input style={inp} value={post.inspiration} disabled={!editable} onChange={e => updatePost(cal, post.id, { inspiration: e.target.value })} /></div>
                      <div><div style={lbl}>Content links (raw footage)</div><input style={inp} value={post.contentLinks} disabled={!editable} onChange={e => updatePost(cal, post.id, { contentLinks: e.target.value })} /></div>
                      <div><div style={lbl}>Reel cover</div><input style={inp} value={post.reelCoverNote} disabled={!editable} onChange={e => updatePost(cal, post.id, { reelCoverNote: e.target.value })} /></div>
                      <div><div style={lbl}>Notes</div><input style={inp} value={post.notes} disabled={!editable} onChange={e => updatePost(cal, post.id, { notes: e.target.value })} /></div>
                      <div><div style={lbl}>Caption</div><textarea style={ta} value={post.caption} disabled={!editable} onChange={e => updatePost(cal, post.id, { caption: e.target.value })} /></div>
                    </div>

                    {/* Designer + reviewer strip */}
                    <div style={{ marginTop: 'auto', padding: 10, borderTop: '1px solid var(--border)', background: 'var(--bg3)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div><div style={lbl}>Post file link</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input style={inp} value={post.fileLink} placeholder="Link for this post…" onChange={e => updatePost(cal, post.id, { fileLink: e.target.value })} disabled={!designer && !canCreate} />
                          {post.fileLink && <a href={post.fileLink} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', display: 'flex', alignItems: 'center' }}><ExternalLink size={16} /></a>}
                        </div>
                      </div>
                      {(post.designedBy || []).length > 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>by {post.designedBy.map(id => empName(employees, id)).join(', ')}</div>}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {designer && <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--text2)', cursor: 'pointer' }}><input type="checkbox" checked={iWorked} onChange={() => toggleWorked(cal, post)} />I worked on this</label>}
                        {designer && !post.awaitingClient && (post.designStatus === 'todo' || post.designStatus === 'changes') && <Btn size="sm" variant="success" onClick={() => submitPost(cal, grid, post)}><Send size={13} />Submit</Btn>}
                        {reviewer && !post.awaitingClient && post.designStatus === 'submitted' && <>
                          <Btn size="sm" variant="success" onClick={() => approvePost(cal, post)}><Check size={13} />Approve</Btn>
                          <Btn size="sm" variant="danger" onClick={() => setReject({ calId: cal.id, postId: post.id, reason: '' })}><AlertTriangle size={13} />Changes</Btn>
                        </>}
                        {reviewer && post.designStatus === 'approved' && <Btn size="sm" variant="ghost" onClick={() => setReject({ calId: cal.id, postId: post.id, reason: '' })}>Re-open</Btn>}
                        {editable && (post.awaitingClient
                          ? <Btn size="sm" variant="success" onClick={() => updatePost(cal, post.id, { awaitingClient: false, awaitingNote: '' })}><Check size={13} />Content received</Btn>
                          : <Btn size="sm" variant="ghost" onClick={() => updatePost(cal, post.id, { awaitingClient: true })}>Mark waiting on client</Btn>)}
                      </div>
                      {post.designStatus === 'changes' && post.reviewNote && (
                        <div style={{ background: 'rgba(192,128,121,0.10)', color: '#C08079', fontSize: 12, padding: '7px 9px', borderRadius: 'var(--radius)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /><span><strong>Changes:</strong> {post.reviewNote}</span>
                        </div>
                      )}
                      {post.awaitingClient && (
                        <div style={{ background: 'rgba(199,154,91,0.10)', color: '#C79A5B', fontSize: 12, padding: '7px 9px', borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={13} style={{ flexShrink: 0 }} /><strong>Waiting on client content</strong></span>
                          {editable
                            ? <input style={inp} value={post.awaitingNote || ''} placeholder="What's needed from the client…" onChange={e => updatePost(cal, post.id, { awaitingNote: e.target.value })} />
                            : (post.awaitingNote && <span>{post.awaitingNote}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {editable && grid.posts.length < 3 && (
                <button onClick={() => addPost(cal)} style={{ border: '1px dashed var(--border2)', borderRadius: 'var(--radius)', background: 'var(--bg2)', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, fontWeight: 600, minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Plus size={18} />Add post
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {editable && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <Btn variant="secondary" onClick={() => addPost(cal)}><Plus size={15} />Add post</Btn>
          <Btn variant="ghost" onClick={() => addGrid(cal)}><Rows3 size={15} />Add row</Btn>
        </div>
      )}

      {cal.status === 'approved' && (
        <Card style={{ padding: 18, marginTop: 16, borderLeft: '3px solid var(--accent)', background: 'rgba(169,137,83,0.06)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <CheckCircle2 size={20} color="var(--accent)" />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Every post is approved. Ready to build the mockup.</span>
          {canCreate && <Btn variant="primary" onClick={() => onBuildMockup && onBuildMockup(cal)} style={{ marginLeft: 'auto' }}><ImageIcon size={14} />Build mockup</Btn>}
        </Card>
      )}

      {/* Assign modal */}
      <Modal open={!!assign} onClose={() => setAssign(null)} title="Assign & create task" width={460}>
        {assign && <>
          <Field label="Assign to designer(s)">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
              {designerEmployees(employees).map(e => {
                const checked = assign.assignedToIds.includes(e.id);
                return (
                  <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', background: checked ? 'rgba(169,137,83,0.12)' : 'transparent' }}>
                    <input type="checkbox" checked={checked} onChange={() => setAssign(a => ({ ...a, assignedToIds: checked ? a.assignedToIds.filter(id => id !== e.id) : [...a.assignedToIds, e.id] }))} />
                    <span style={{ fontSize: 13 }}>{e.name} <span style={{ color: 'var(--text3)' }}>· {e.role}</span></span>
                  </label>
                );
              })}
              {designerEmployees(employees).length === 0 && <span style={{ fontSize: 13, color: 'var(--text3)' }}>No designers found.</span>}
            </div>
          </Field>
          <Field label="Due date"><Input type="date" value={assign.dueDate} onChange={e => setAssign(a => ({ ...a, dueDate: e.target.value }))} /></Field>
          <Field label="Extra info (optional)"><textarea style={ta} value={assign.notes} onChange={e => setAssign(a => ({ ...a, notes: e.target.value }))} placeholder="Anything the designer needs to know…" /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn onClick={() => setAssign(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={confirmAssign}>Assign & create task</Btn>
          </div>
        </>}
      </Modal>

      {/* Reject reason modal */}
      <Modal open={!!reject} onClose={() => setReject(null)} title="Request changes" width={440}>
        {reject && <>
          <Field label="What needs to change?"><textarea style={ta} autoFocus value={reject.reason} onChange={e => setReject(r => ({ ...r, reason: e.target.value }))} placeholder="Be specific — this goes to the designer." /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn onClick={() => setReject(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={confirmReject}>Send change request</Btn>
          </div>
        </>}
      </Modal>
    </div>
  );
}
